import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';

import { DonutChart } from '../../../components/charts/DonutChart';
import { InvestmentsVsDebtCard } from '../../../components/charts/InvestmentsVsDebtCard';
import { NetWorthYearTable } from '../../../components/charts/NetWorthYearTable';
import { formatCurrency, formatK } from '../../../components/charts/d3/format';
import {
  HomeLoanCard,
  NetWorthStrip,
} from '../../../components/home';
import { WidgetInsightsSlot } from '../../../components/assistant/WidgetInsightCallout';
import {
  CompoundingExplorer,
  EditablePortfolioList,
  InvestmentHoldingsTable,
  PortfolioAccountsCard,
  WealthAccountTrends,
} from '../../../components/wealth';
import { PageHeader } from '../../../components/layout/PageHeader';
import { WebScreenBody } from '../../../components/layout/WebScreenBody';
import { WebWidgetCell, WebWidgetGrid } from '../../../components/layout/WebWidgetGrid';
import { ErrorRetry } from '../../../components/ui/ErrorRetry';
import { GlassButton } from '../../../components/ui/GlassButton';
import { GlassCard } from '../../../components/ui/GlassCard';
import { RefreshButton } from '../../../components/ui/RefreshButton';
import { WidgetRefreshScope } from '../../../contexts/WidgetRefreshContext';
import { SampleDataBanner } from '../../../components/ui/SampleDataBanner';
import { useHousehold } from '../../../contexts/HouseholdContext';
import { useImportOnboarding } from '../../../contexts/ImportOnboardingContext';
import { useLiabilities } from '../../../contexts/LiabilityContext';
import { usePortfolio } from '../../../contexts/PortfolioContext';
import { useSampleData } from '../../../contexts/SampleDataContext';
import { navigateToLoan } from '../../../core/navigation/mainTabLinks';
import type { MainTabParamList } from '../../../core/navigation/types';
import { colors, spacing, typography } from '../../../core/theme/tokens';
import { useAppRefresh } from '../../../hooks/useAppRefresh';
import { useHomeDashboard } from '../../../hooks/useHomeDashboard';
import { useInvestmentCompounding } from '../../../hooks/useInvestmentCompounding';

export function WealthTabScreen() {
  const navigation = useNavigation<BottomTabNavigationProp<MainTabParamList>>();
  const { isSampleMode } = useSampleData();
  const { openImportMore } = useImportOnboarding();
  const { openAddAccount } = usePortfolio();
  const { core } = useHousehold();
  const { netWorth, projections, usingDraftPreview, isLive, error } = useHomeDashboard();
  const { error: portfolioError } = usePortfolio();
  const { error: liabilityError } = useLiabilities();
  const { refreshAll, refreshing } = useAppRefresh();
  const startYear = Number(core.profile.asOfMonth.slice(0, 4)) || new Date().getFullYear();
  const compounding = useInvestmentCompounding(netWorth.assets, { startYear });

  const totalAssets = netWorth.activeInvestments + netWorth.passiveInvestments;
  const openLoan = () => navigateToLoan(navigation);
  const pageRefresh = () => {
    void refreshAll('wealth_page');
  };
  const pageError = error || portfolioError || liabilityError;

  return (
    <WebScreenBody>
      {isSampleMode && <SampleDataBanner />}

      <PageHeader
        title="Wealth"
        subtitle={
          usingDraftPreview
            ? 'Your draft preview on the sample balance sheet'
            : 'Net worth, assets, and liabilities'
        }
        onRefresh={pageRefresh}
        refreshing={refreshing}
      />

      <WidgetRefreshScope refresh={pageRefresh} refreshing={refreshing}>
      {pageError ? (
        <ErrorRetry message={pageError} onRetry={pageRefresh} refreshing={refreshing} />
      ) : null}

      <WebWidgetGrid>
        <WebWidgetCell>
          <GlassCard style={styles.heroCard}>
            <View style={styles.heroHeader}>
              <Text style={styles.label}>Net worth today</Text>
              <RefreshButton compact />
            </View>
            <Text style={styles.heroValue}>{formatCurrency(netWorth.total)}</Text>
            <Text style={styles.heroSub}>
              {formatCurrency(totalAssets)} assets ·{' '}
              {formatCurrency(netWorth.totalLiabilities)} liabilities
            </Text>
          </GlassCard>
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <NetWorthStrip
            activeInvestments={netWorth.activeInvestments}
            passiveInvestments={netWorth.passiveInvestments}
            totalLiabilities={netWorth.totalLiabilities}
            total={netWorth.total}
            onActiveInvestmentsPress={isSampleMode ? undefined : openImportMore}
            onOtherAssetsPress={
              isSampleMode ? undefined : () => openAddAccount({ isInvestment: false })
            }
          />
        </WebWidgetCell>

        {(totalAssets > 0 || netWorth.totalLiabilities > 0) && (
          <WebWidgetCell>
            <GlassCard>
              <Text style={styles.sectionTitle}>Assets vs liabilities</Text>
              <DonutChart
                size={152}
                segments={[
                  {
                    label: 'Assets',
                    value: Math.max(totalAssets, 0),
                    color: colors.accent,
                  },
                  {
                    label: 'Liabilities',
                    value: Math.max(netWorth.totalLiabilities, 0),
                    color: colors.warning,
                  },
                ]}
                centerLabel="Net worth"
                centerValue={formatK(netWorth.total)}
              />
            </GlassCard>
          </WebWidgetCell>
        )}

        <WebWidgetCell span="full">
          <EditablePortfolioList />
          {isLive ? (
            <WidgetInsightsSlot widget="wealth_portfolio" style={styles.widgetInsight} />
          ) : null}
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <PortfolioAccountsCard title="Other assets" category="other" />
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <InvestmentHoldingsTable
            holdings={compounding.holdings}
            principal={compounding.principal}
            weightedRatePct={compounding.seedExpectedRatePct}
            annualContribution={compounding.annualContribution}
          />
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <WealthAccountTrends />
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <CompoundingExplorer model={compounding} />
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <GlassButton
            variant="secondary"
            onPress={() => navigation.navigate('Grow')}
            accessibilityLabel="Open Grow tab for full compound table"
          >
            Open full Grow table
          </GlassButton>
        </WebWidgetCell>

        {netWorth.liabilities.length > 0 && (
          <WebWidgetCell>
            <GlassCard>
              <Text style={styles.sectionTitle}>Liabilities</Text>
              <Text style={styles.sectionHint}>Balances on your file</Text>
              <View style={styles.list}>
                {netWorth.liabilities.map((liability) => (
                  <View key={liability.id} style={styles.row}>
                    <View style={styles.meta}>
                      <Text style={styles.rowLabel}>{liability.label}</Text>
                      {liability.interestRatePct != null && (
                        <Text style={styles.rowHint}>{liability.interestRatePct}% APR</Text>
                      )}
                    </View>
                    <Text style={[styles.rowValue, styles.warning]}>
                      −{formatCurrency(liability.balance)}
                    </Text>
                  </View>
                ))}
              </View>
              {isLive ? (
                <WidgetInsightsSlot widget="debt_overview" style={styles.widgetInsight} />
              ) : null}
            </GlassCard>
          </WebWidgetCell>
        )}

        <WebWidgetCell span="full">
          <HomeLoanCard onPress={openLoan} />
          {isLive && netWorth.liabilities.length === 0 ? (
            <WidgetInsightsSlot widget="debt_overview" style={styles.widgetInsight} />
          ) : null}
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <InvestmentsVsDebtCard
            holdings={netWorth.assets
              .filter((a) => a.isInvestment)
              .map((a) => ({
                value: a.value,
                expectedReturnPct: a.expectedReturnPct,
              }))}
            mortgageBalanceSeries={projections.mortgageBalanceSeries}
            startYear={startYear}
          />
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <GlassCard>
            <View style={styles.heroHeader}>
              <Text style={styles.sectionTitle}>Year by year</Text>
              <RefreshButton compact />
            </View>
            <Text style={styles.sectionHint}>
              {formatCurrency(projections.netWorthIn10Years)} projected in 10 years
            </Text>
            <NetWorthYearTable rows={projections.netWorthByYear} maxRows={6} />
          </GlassCard>
        </WebWidgetCell>
      </WebWidgetGrid>
      </WidgetRefreshScope>
    </WebScreenBody>
  );
}

const styles = StyleSheet.create({
  heroCard: {
    gap: spacing.xs,
  },
  heroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  label: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  heroValue: {
    ...typography.display,
    color: colors.accent,
    fontSize: 32,
    fontVariant: ['tabular-nums'],
  },
  heroSub: {
    ...typography.body,
    color: colors.textMuted,
  },
  sectionTitle: {
    ...typography.headline,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  sectionHint: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.md,
  },
  list: {
    gap: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  meta: {
    flex: 1,
    gap: 2,
  },
  rowLabel: {
    ...typography.body,
    color: colors.text,
  },
  rowHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  rowValue: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  warning: {
    color: colors.warning,
  },
  widgetInsight: {
    marginTop: spacing.sm,
  },
});
